// frontend/src/services/teamManagerService.js
// Service layer for the Team Manager feature.
// Reads/writes are RLS-gated; sync calls go through the edge function.
import { supabase } from './supabaseClient';

export const teamManagerService = {
    list: async () => {
        const { data, error } = await supabase
            .from('team_managers')
            .select('*')
            .order('created_at', { ascending: false });
        if (error) throw error;
        return data || [];
    },

    create: async ({ email, nationality_iso3, nationality_ioc }) => {
        const trimmedIso3 = (nationality_iso3 || '').trim().toUpperCase();
        const trimmedIoc = (nationality_ioc || '').trim().toUpperCase();

        if (!trimmedIso3 && !trimmedIoc) {
            throw new Error('At least one nationality code (ISO3 or IOC) is required');
        }

        const payload = {
            email: email.trim().toLowerCase(),
            nationality_iso3: trimmedIso3 || null,
            nationality_ioc: trimmedIoc || null,
        };

        const { data, error } = await supabase
            .from('team_managers')
            .insert(payload)
            .select()
            .single();

        if (error) {
            // Pretty-print common cases
            if (error.code === '23505') {
                throw new Error('A team manager with this email already exists');
            }
            throw error;
        }
        return data;
    },

    remove: async (id) => {
        const { error } = await supabase
            .from('team_managers')
            .delete()
            .eq('id', id);
        if (error) throw error;
    },

    /**
     * Trigger a Sportradar sync for one nationality code.
     * Returns the edge function payload: { codes_synced, persons_upserted, per_code }
     */
    syncNationality: async (code) => {
        const { data, error } = await supabase.functions.invoke('sync-sportradar-persons', {
            body: { nationality: code }
        });
        if (error) throw error;
        if (!data?.success) {
            throw new Error(data?.error || 'Sync failed');
        }
        return data.data;
    },

    /**
     * Magic-link sign-in. Supabase sends an email; the link redirects back here.
     */
    requestMagicLink: async (email, redirectTo) => {
        const { error } = await supabase.auth.signInWithOtp({
            email: email.trim().toLowerCase(),
            options: {
                emailRedirectTo: redirectTo,
                shouldCreateUser: true,
            },
        });
        if (error) throw error;
    },

    /**
     * Fetch the logged-in caller's team_managers row.
     * For team managers, RLS already scopes to their own row. We add an explicit
     * email filter so that super_admin (who has full read access via a separate
     * RLS policy) doesn't see all team_managers rows here and break .maybeSingle().
     */
    getMyTeam: async () => {
        const { data: userData } = await supabase.auth.getUser();
        const email = userData?.user?.email?.toLowerCase();
        if (!email) return null;

        const { data, error } = await supabase
            .from('team_managers')
            .select('*')
            .ilike('email', email)
            .maybeSingle();
        if (error) throw error;
        return data || null;
    },

    /**
     * Fetch the team manager's full roster from the locally synced Sportradar data.
     * RLS scopes the result to the user's nationality codes automatically.
     * Includes raw_data so the UI can extract the photo-specific timestamp from
     * images[] (sr_updated is the person record's update, not the photo's).
     */
    getMyRoster: async () => {
        const { data, error } = await supabase
            .from('sportradar_persons')
            .select('person_id, name_full_latin, name_full_local, nationality, dob, gender, status, current_photo_url, sr_updated, synced_at, raw_data')
            .order('name_full_latin', { ascending: true })
            .range(0, 9999);
        if (error) throw error;
        return data || [];
    },

    /**
     * Upload a photo file to Supabase storage and push it to Sportradar.
     * Returns the Supabase storage public URL; we use this for the post-upload
     * mirror patch (sync-sportradar-photo's response.newImageUrl is null in practice
     * because Sportradar's CDN takes time to register the upload).
     */
    uploadPhotoForPerson: async ({ personId, nationality, file }) => {
        if (!file) throw new Error('No file provided');
        if (!personId) throw new Error('Missing personId');
        // Defense in depth: personId becomes part of the storage path; reject
        // anything that could escape the team-manager-uploads/<nat>/ prefix.
        // SR-issued personIds are alphanumeric + hyphen in practice.
        if (!/^[A-Za-z0-9-]+$/.test(personId)) {
            throw new Error('Invalid personId format');
        }

        const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
        const safeNationality = (nationality || 'unknown').toUpperCase();
        const fileName = `team-manager-uploads/${safeNationality}/${personId}_${Date.now()}.${ext}`;

        // 1) Upload to Supabase storage
        const { error: storageError } = await supabase.storage
            .from('player-photos')
            .upload(fileName, file, {
                contentType: file.type || 'image/jpeg',
                upsert: true,
            });
        if (storageError) throw new Error(`Storage error: ${storageError.message}`);

        const { data: urlData } = supabase.storage
            .from('player-photos')
            .getPublicUrl(fileName);
        const photoUrl = urlData?.publicUrl;
        if (!photoUrl) throw new Error('Failed to obtain public URL');

        // 2) Push to Sportradar via existing edge function
        const { data, error } = await supabase.functions.invoke('sync-sportradar-photo', {
            body: { personId, photoUrl }
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        return photoUrl;
    },

    /**
     * Patch the locally synced Sportradar data with a freshly-uploaded photo URL. Avoids re-fetching
     * the full nationality from Sportradar after every photo upload.
     */
    updatePersonPhoto: async ({ personId, newPhotoUrl }) => {
        if (!personId || !newPhotoUrl) throw new Error('Missing personId or newPhotoUrl');
        const { data, error } = await supabase.functions.invoke('update-sr-person-photo', {
            body: { personId, newPhotoUrl }
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        return data?.data || null;
    },

    /**
     * Aggregate overview across all the given nationality codes (super-admin KPI strip).
     * Uses three head:true queries with .in() so we never pull row data:
     *   1) total persons synced
     *   2) persons with a REAL photo (URL set AND not a 130x130 migration silhouette)
     *   3) most-recent synced_at timestamp
     *
     * The silhouette filter matches TeamRosterPage's hasRealPhoto():
     *   raw_data.images must NOT contain a PERSON_HEAD entry with maximumWidth=130
     *   and maximumHeight=130 (the dimensions assigned to placeholder images during
     *   the original Sportradar migration). We express this via PostgREST's `cs`
     *   (jsonb @>) operator, negated.
     */
    getOverviewStats: async (codes) => {
        const unique = [...new Set((codes || []).filter(Boolean))];
        if (unique.length === 0) {
            return { total: 0, withPhoto: 0, lastSyncedAt: null };
        }

        const SILHOUETTE_PATTERN = '[{"imageType":"PERSON_HEAD","maximumWidth":130,"maximumHeight":130}]';

        const [totalRes, withPhotoRes, latestRes] = await Promise.all([
            supabase
                .from('sportradar_persons')
                .select('*', { count: 'exact', head: true })
                .in('nationality', unique),
            supabase
                .from('sportradar_persons')
                .select('*', { count: 'exact', head: true })
                .in('nationality', unique)
                .not('current_photo_url', 'is', null)
                .not('raw_data->images', 'cs', SILHOUETTE_PATTERN),
            // The newest recorded sync run, not max(synced_at). A run only touches rows
            // whose data actually changed, so over unchanged records a perfectly healthy
            // sync leaves every synced_at untouched — and the staleness warning built on
            // it would report a failure that never happened.
            supabase
                .from('sync_runs')
                .select('finished_at')
                .eq('kind', 'sportradar_persons')
                .eq('ok', true)
                .order('finished_at', { ascending: false })
                .limit(1)
                .maybeSingle(),
        ]);

        return {
            total: totalRes.count || 0,
            withPhoto: withPhotoRes.count || 0,
            lastSyncedAt: latestRes.data?.finished_at || null,
        };
    },

    /**
     * Synced-person counts per nationality code. Uses head:true so it doesn't pull rows.
     * Returns an object: { 'MAS': 142, 'MYS': 89, ... }
     */
    countPerNationality: async (codes) => {
        const unique = [...new Set((codes || []).filter(Boolean))];
        if (unique.length === 0) return {};

        const results = await Promise.all(
            unique.map(code =>
                supabase
                    .from('sportradar_persons')
                    .select('*', { count: 'exact', head: true })
                    .eq('nationality', code)
                    .then(({ count, error }) => ({
                        code,
                        count: error ? 0 : (count || 0)
                    }))
            )
        );

        const out = {};
        results.forEach(r => { out[r.code] = r.count; });
        return out;
    },
};
